import deepmerge from 'deepmerge';
import yaml from 'js-yaml';

// Statically reference known merge files so they are bundled and addressable at runtime
const BASE_OVERRIDE_URL = new URL('./merge/base.yaml', import.meta.url).toString();


function isJson(text: string): boolean {
  const first = text.trim()[0];
  return first === '{' || first === '[';
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  return await res.text();
}

function resolveUrlMaybeRelative(inputUrl: string): string {
  try {
    // Absolute URL
    // eslint-disable-next-line no-new
    new URL(inputUrl);
    return inputUrl;
  } catch {
    // Relative to app origin
    const base = window.location.origin + window.location.pathname;
    return new URL(inputUrl, base).toString();
  }
}

function parseSpec(text: string): any {
  if (isJson(text)) {
    return JSON.parse(text);
  }
  return yaml.load(text);
}

export async function loadAndMergeOpenApi(baseUrl: string): Promise<any> {
  const resolvedBase = resolveUrlMaybeRelative(baseUrl);

  // Determine filename of the requested spec to pick the per-API override
  const filename = (() => {
    try {
      const u = new URL(resolvedBase);
      const pathname = u.pathname;
      const parts = pathname.split('/');
      return parts[parts.length - 1];
    } catch {
      const parts = resolvedBase.split('/');
      return parts[parts.length - 1];
    }
  })();


  // Fetch base spec and any available overrides in parallel
  const fetches: Array<Promise<string | undefined>> = [
    fetchText(resolvedBase).then(t => t),
    fetchText(BASE_OVERRIDE_URL).then(t => t).catch(() => undefined),
    fetchText(new URL(`./merge/${filename}`, import.meta.url).toString()).then(t => t).catch(() => undefined),
  ];

  const [baseText, baseOverrideText, perApiOverrideText] = await Promise.all(fetches);

  const baseDoc = parseSpec(baseText as string);
  const withBaseOverride = baseOverrideText ? deepmerge(baseDoc, parseSpec(baseOverrideText), { arrayMerge: (_d, s) => s }) : baseDoc;
  const withPerApiOverride = perApiOverrideText ? deepmerge(withBaseOverride, parseSpec(perApiOverrideText), { arrayMerge: (_d, s) => s }) : withBaseOverride;

  return withPerApiOverride;
}


